import {
  subclass,
  property
} from "@arcgis/core/core/accessorSupport/decorators";


import LevelAware from "./LevelAware";

// ┌────────────┐
// │ Interfaces │
// └────────────┘

/**
 * Administrative Portal Privileges: "portal:admin:*"
 */
interface RolePrivilegesPortalAdmin {
  inviteUsers: boolean; // "portal:admin:inviteUsers"
  disableUsers: boolean; // "portal:admin:disableUsers"
  viewUsers: boolean; // "portal:admin:viewUsers"
  updateUsers: boolean; // "portal:admin:updateUsers"
  deleteUsers: boolean; // "portal:admin:deleteUsers"
  changeUserRoles: boolean; // "portal:admin:changeUserRoles"
  viewGroups: boolean; // "portal:admin:viewGroups"
  updateGroups: boolean; // "portal:admin:updateGroups"
  deleteGroups: boolean; // "portal:admin:deleteGroups"
  reassignGroups: boolean; // "portal:admin:reassignGroups"
  assignToGroups: boolean; // "portal:admin:assignToGroups"
  manageEnterpriseGroups: boolean; // "portal:admin:manageEnterpriseGroups"
  viewItems: boolean; // "portal:admin:viewItems"
  updateItems: boolean; // "portal:admin:updateItems"
  deleteItems: boolean; // "portal:admin:deleteItems"
  reassignItems: boolean; // "portal:admin:reassignItems"
  manageLicenses: boolean; // "portal:admin:manageLicenses"
  shareToGroup: boolean; // "portal:admin:shareToGroup"
  shareToOrg: boolean; // "portal:admin:shareToOrg"
  shareToPublic: boolean; // "portal:admin:shareToPublic"
  createUpdateCapableGroup: boolean; // "portal:admin:createUpdateCapableGroup"
  updateItemCategorySchema: boolean; // "portal:admin:updateItemCategorySchema"
  manageCollaborations: boolean; // "portal:admin:manageCollaborations"
  manageSecurity: boolean; // "portal:admin:manageSecurity"
  manageWebsite: boolean; // "portal:admin:manageWebsite"
  manageRoles: boolean; // "portal:admin:manageRoles"
  manageServers: boolean; // "portal:admin:manageServers"
  manageUtilityServices: boolean; // "portal:admin:manageUtilityServices"
  manageCredits: boolean; // "potal:admin:manageCredits"
}

/**
 * Publisher Portal Privileges: "portal:publisher:*"
 */
interface RolePrivilegesPortalPublisher {
  publishFeatures: boolean; // "portal:publisher:publishFeatures"
  publishTiles: boolean; // "portal:publisher:publishTiles"
  publishScenes: boolean; // "portal:publisher:publishScenes"
  publishServerServices: boolean; // "portal:publisher:publishServerServices",
  publishServerGPServices: boolean; // "portal:publisher:publishServerGPServices",
  registerDataStores: boolean; // "portal:publisher:registerDataStores"
  bulkPublishFromDataStores: boolean; // "portal:publisher:bulkPublishFromDataStores"
  enumerateDataStores: boolean; // "portal:publisher:enumerateDataStores"
}

/**
 * User Portal Privileges: "portal:user:*"
 */
interface RolePrivilegesPortalUser {
  createGroup: boolean; // "portal:user:createGroup"
  joinGroup: boolean; // "portal:user:joinGroup"
  joinNonOrgGroup: boolean; // "portal:user:joinNonOrgGroup"
  createItem: boolean; // "portal:user:createItem"
  shareToGroup: boolean; // "portal:user:shareToGroup"
  shareToOrg: boolean; // "portal:user:shareToOrg"
  shareToPublic: boolean; // "portal:user:shareToPublic"
  shareGroupToOrg: boolean; // "portal:user:shareGroupToOrg"
  shareGroupToPublic: boolean; // "portal:user:shareGroupToPublic"
  viewOrgUsers: boolean; // "portal:user:viewOrgUsers"
  viewOrgItems: boolean; // "portal:user:viewOrgItems"
  viewOrgGroups: boolean; // "portal:user:viewOrgGroups"
  viewTracks: boolean; // "portal:user:viewTracks"
}

/**
 * Portal Privileges: "portal:*"
 */
interface RolePrivilegesPortal {
  admin: RolePrivilegesPortalAdmin;
  publisher: RolePrivilegesPortalPublisher;
  user: RolePrivilegesPortalUser;
}

/**
 * Features User Privileges: "features:user:*"
 */
interface RolePrivilegesFeaturesUsers {
  edit: boolean; // "features:user:edit"
  fullEdit: boolean; // "features:user:fullEdit"
}

/**
 * Features Privileges: "features:*"
 */
interface RolePrivilegesFeatures {
  user: RolePrivilegesFeaturesUsers;
}

/**
 * Open Data User Privileges: "opendata:user:*"
 */
interface RolePrivilegesOpenDataUser {
  openDataAdmin: boolean; // "opendata:user:openDataAdmin"
  designateGroup: boolean; // "opendata:user:designateGroup"
}

/**
 * Open Data Privileges: "opendata:*"
 */
interface RolePrivilegesOpenData {
  user: RolePrivilegesOpenDataUser;
}

/**
 * Premium User Privileges: "premium:user:*"
 */
interface RolePrivilegesPremiumUser {
  geocode: boolean; // "premium:user:geocode"
  networkanalysis: boolean; // "premium:user:networkanalysis"
  spatialanalysis: boolean; // "premium:user:spatialanalysis"
  geoenrichment: boolean; // "premium:user:geoenrichment"
  demographics: boolean; // "premium:user:demographics"
  // Removed - https://devtopia.esri.com/WebGIS/arcgis-portal-app/issues/21382
  // elevation: boolean; // "premium:user:elevation"
  featurereport: boolean; // "premium:user:featurereport"
}

/**
 * Premium Publisher Privileges: "premium:publisher:*"
 */
interface RolePrivilegesPremiumPublisher {
  geoanalytics: boolean; // "premium:publisher:geoanalytics"
  rasteranalysis: boolean; // "premium:publisher:rasteranalysis"
  createNotebooks: boolean; // "premium:publisher:createNotebooks"
  scheduleNotebooks: boolean; // "premium:publisher:scheduleNotebooks"
  publishNotebooks: boolean; // "premium:publisher:publishNotebooks"
  createAdvancedNotebooks: boolean; // "premium:publisher:createAdvancedNotebooks"
}

/**
 * Premium Privileges: "premium:*"
 */
interface RolePrivilegesPremium {
  user: RolePrivilegesPremiumUser;
  publisher: RolePrivilegesPremiumPublisher;
}

/**
 * Marketplace Admin Privileges: "marketplace:admin:*"
 */
interface RolePrivilegesMarketplaceAdmin {
  manage: boolean; // "marketplace:admin:manage"
  purchase: boolean; // "marketplace:admin:purchase"
  startTrial: boolean; // "marketplace:admin:startTrial"
}

/**
 * Marketplace Privileges: "marketplace:*"
 */
interface RolePrivilegesMarketplace {
  admin: RolePrivilegesMarketplaceAdmin;
}

/**
 * All Privileges.
 */
interface RolePrivileges {
  portal: RolePrivilegesPortal; // "portal:*"
  features: RolePrivilegesFeatures; // "features:*"
  opendata: RolePrivilegesOpenData; // "opendata:*"
  premium: RolePrivilegesPremium; // "premium:*"
  marketplace: RolePrivilegesMarketplace; // "marketplace:*"
}

/**
 * Role Parameters for constructor.
 */
interface RoleParams {
  portalUser?: any; // TODO - look into usign arcgisonline/pages/portal/PortalUser
  id?: string;
  role: string;
  roleId?: string;
  name?: string;
  description?: string;
}

interface Role extends LevelAware {}

/**
 * Defines a user role and the privileges granted to the role.
 */
@subclass()
class Role extends LevelAware {
  // ┌────────────┐
  // │ Properties │
  // └────────────┘

  id: string = null;
  baseRole: string = null;
  name: string = null;
  description: string = null;
  privileges: string[] = [];

  protected privilegeObj: RolePrivileges = {
    portal: {
      admin: {
        // "portal:admin:*"
        inviteUsers: false, // "portal:admin:inviteUsers"
        disableUsers: false, // "portal:admin:disableUsers"
        viewUsers: false, // "portal:admin:viewUsers"
        updateUsers: false, // "portal:admin:updateUsers"
        deleteUsers: false, // "portal:admin:deleteUsers"
        changeUserRoles: false, // "portal:admin:changeUserRoles"
        viewGroups: false, // "portal:admin:viewGroups"
        updateGroups: false, // "portal:admin:updateGroups"
        deleteGroups: false, // "portal:admin:deleteGroups"
        reassignGroups: false, // "portal:admin:reassignGroups"
        assignToGroups: false, // "portal:admin:assignToGroups"
        manageEnterpriseGroups: false, // "portal:admin:manageEnterpriseGroups"
        viewItems: false, // "portal:admin:viewItems"
        updateItems: false, // "portal:admin:updateItems"
        deleteItems: false, // "portal:admin:deleteItems"
        reassignItems: false, // "portal:admin:reassignItems"
        manageLicenses: false, // "portal:admin:manageLicenses"
        shareToGroup: false, // "portal:admin:shareToGroup"
        shareToOrg: false, // "portal:admin:shareToOrg"
        shareToPublic: false, // "portal:admin:shareToPublic"
        createUpdateCapableGroup: false, // "portal:admin:createUpdateCapableGroup"
        updateItemCategorySchema: false, // "portal:admin:updateItemCategorySchema"
        manageCollaborations: false, // "portal:admin:manageCollaborations"
        manageSecurity: false, // "portal:admin:manageSecurity"
        manageWebsite: false, // "portal:admin:manageWebsite"
        manageRoles: false, // "portal:admin:manageRoles"
        manageServers: false, // "portal:admin:manageServers"
        manageUtilityServices: false, // "portal:admin:manageUtilityServices"
        manageCredits: false // "potal:admin:manageCredits"
      },
      publisher: {
        publishFeatures: false, // "portal:publisher:publishFeatures"
        publishTiles: false, // "portal:publisher:publishTiles"
        publishScenes: false, // "portal:publisher:publishScenes"
        publishServerServices: false, // "portal:publisher:publishServerServices"
        publishServerGPServices: false, // "portal:publisher:publishServerGPServices"
        registerDataStores: false, // "portal:publisher:registerDataStores"
        bulkPublishFromDataStores: false, // "portal:publisher:bulkPublishFromDataStores"
        enumerateDataStores: false // "portal:publisher:enumerateDataStores"
      },
      user: {
        // "portal:user:*"
        createGroup: false, // "portal:user:createGroup"
        joinGroup: false, // "portal:user:joinGroup"
        joinNonOrgGroup: false, // "portal:user:joinNonOrgGroup"
        createItem: false, // "portal:user:createItem"
        shareToGroup: false, // "portal:user:shareToGroup"
        shareToOrg: false, // "portal:user:shareToOrg"
        shareToPublic: false, // "portal:user:shareToPublic"
        shareGroupToOrg: false, // "portal:user:shareGroupToOrg"
        shareGroupToPublic: false, // "portal:user:shareGroupToPublic"
        viewOrgUsers: false, // "portal:user:viewOrgUsers"
        viewOrgItems: false, // "portal:user:viewOrgItems"
        viewOrgGroups: false, // "portal:user:viewOrgGroups",
        viewTracks: false // "portal:user:viewTracks"
      }
    },
    features: {
      // "features:*"
      user: {
        // "features:user:*"
        edit: false, // "features:user:edit"
        fullEdit: false // "features:user:fullEdit"
      }
    },
    opendata: {
      // "opendata:*"
      user: {
        // "opendata:user:*"
        openDataAdmin: false, // "opendata:user:openDataAdmin"
        designateGroup: false // "opendata:user:designateGroup"
      }
    },
    premium: {
      // "premium:*"
      user: {
        // "premium:user:*"
        geocode: false, // "premium:user:geocode"
        networkanalysis: false, // "premium:user:networkanalysis"
        spatialanalysis: false, // "premium:user:spatialanalysis"
        geoenrichment: false, // "premium:user:geoenrichment"
        demographics: false, // "premium:user:demographics"
        // Removed - https://devtopia.esri.com/WebGIS/arcgis-portal-app/issues/21382
        // elevation: false, // "premium:user:elevation"
        featurereport: false // "premium:user:featurereport"
      },
      publisher: {
        geoanalytics: false, // "premium:publisher:geoanalytics"
        rasteranalysis: false, // "premium:publisher:rasteranalysis"
        createNotebooks: false, // "premium:publisher:createNotebooks"
        scheduleNotebooks: false, // "premium:publisher:scheduleNotebooks"
        publishNotebooks: false, // "premium:publisher:publishNotebooks"
        createAdvancedNotebooks: false // "premium:publisher:createAdvancedNotebooks"
      }
    },
    marketplace: {
      // "marketplace:*"
      admin: {
        // "marketplace:admin:*"
        manage: false, // "marketplace:admin:manage"
        purchase: false, // "marketplace:admin:purchase"
        startTrial: false // "marketplace:admin:startTrial"
      }
    }
  };

  constructor(params: RoleParams | any) {
    super();
    params = params || {};
    const portalUser: any = params.portalUser;
    if (!portalUser) {
      this.id = params.id;
      this.baseRole = params.role;
      this.name = params.name;
      this.description = params.description;
      if (params.privileges) {
        this.setPrivileges(params.privileges);
      }
    } else {
      this.id = portalUser.roleId ? portalUser.roleId : portalUser.role;
      this.baseRole = portalUser.role;
      this.setPrivileges(portalUser.privileges);
    }
  }

  // ┌────────┐
  // │ Public │
  // └────────┘

  /**
   * Checks whether the role is a core Viewer role. Based on the role id.
   * @returns {boolean}
   */
  isViewer(): boolean {
    return this.id && this.id === this.SYSTEM_ROLES.VIEWER;
  }

  /**
   * Checks whether the role is a core User role. Based on the role id.
   * @returns {boolean}
   */
  isUser(): boolean {
    return this.id && (this.id === "org_user" || this.id === "account_user");
  }

  /**
   * Checks whether the role is a core Publisher role. Based on the role id.
   * @returns {boolean}
   */
  isPublisher(): boolean {
    return (
      this.id &&
      (this.id === "org_publisher" || this.id === "account_publisher")
    );
  }

  /**
   * Checks whether the role is a core Administrator role. Based on the role id.
   * @returns {boolean}
   */
  isAdmin(): boolean {
    return this.id && (this.id === "org_admin" || this.id === "account_admin");
  }

  /**
   * Checks whether the role is based on the core User role. Also returns true if it also _is_ the core User role.
   * @returns {boolean}
   */
  isBasedOnUser(): boolean {
    return (
      this.isUser() ||
      (this.baseRole &&
        (this.baseRole === "org_user" || this.baseRole === "account_user"))
    );
  }

  /**
   * Checks whether the role is based on the core Publisher role. Also returns true if it also _is_ the core Publisher role.
   * @returns {boolean}
   */
  isBasedOnPublisher(): boolean {
    return (
      this.isPublisher() ||
      (this.baseRole &&
        (this.baseRole === "org_publisher" ||
          this.baseRole === "account_publisher"))
    );
  }

  /**
   * Checks whether the role is based on the core Administrator role. Also returns true if it also _is_ the core Administrator role.
   * @returns {boolean}
   */
  isBasedOnAdmin(): boolean {
    return (
      this.isAdmin() ||
      (this.baseRole &&
        (this.baseRole === "org_admin" || this.baseRole === "account_admin"))
    );
  }

  /**
   * Checks whether the role is a custom role. Based on the role id.
   * @returns {boolean}
   */
  isCustom(): boolean {
    return (
      !this.isUser() &&
      !this.isPublisher() &&
      !this.isAdmin() &&
      this.id &&
      this.id.length > 0
    );
  }

  /**
   * Checks whether the privilege "portal:admin:inviteUsers" is granted.
   * @returns {boolean}
   */
  canInviteUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.inviteUsers;
  }

  /**
   * Checks whether the privilege "portal:admin:disableUsers" is granted.
   * @returns {boolean}
   */
  canDisableUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.disableUsers;
  }

  /**
   * Checks whether the privilege "portal:admin:viewUsers" is granted.
   * @returns {boolean}
   */
  canViewUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.viewUsers;
  }

  /**
   * Checks whether the privilege "portal:admin:updateUsers" is granted.
   * @returns {boolean}
   */
  canUpdateUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.updateUsers;
  }

  /**
   * Checks whether the privilege "portal:admin:deleteUsers" is granted.
   * @returns {boolean}
   */
  canDeleteUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.deleteUsers;
  }

  /**
   * Checks whether the privilege "portal:admin:changeUserRoles" is granted.
   * @returns {boolean}
   */
  canChangeUserRoles(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.changeUserRoles;
  }

  /**
   * Checks whether the privilege "portal:admin:viewGroups" is granted.
   * @returns {boolean}
   */
  canViewOrgGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.viewGroups;
  }

  /**
   * Checks whether the privilege "portal:admin:updateGroups" is granted.
   * @returns {boolean}
   */
  canUpdateOrgGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.updateGroups;
  }

  /**
   * Checks whether the privilege "portal:admin:deleteGroups" is granted.
   * @returns {boolean}
   */
  canDeleteOrgGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.deleteGroups;
  }

  /**
   * Checks whether the privilege "portal:admin:reassignGroups" is granted.
   * @returns {boolean}
   */
  canReassignOrgGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.reassignGroups;
  }

  /**
   * Checks whether the privilege "portal:admin:assignToGroups" is granted.
   * @returns {boolean}
   */
  canAssignUsersToOrgGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.assignToGroups;
  }

  /**
   * Checks whether the privilege "portal:admin:manageEnterpriseGroups" is granted.
   * @returns {boolean}
   */
  canManageEnterpriseGroups(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.admin.manageEnterpriseGroups
    );
  }

  /**
   * Checks whether the privilege "portal:admin:viewItems" is granted.
   * @returns {boolean}
   */
  canViewOrgItems(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.viewItems;
  }

  /**
   * Checks whether the privilege "portal:admin:updateItems" is granted.
   * @returns {boolean}
   */
  canUpdateOrgItems(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.updateItems;
  }

  /**
   * Checks whether the privilege "portal:admin:deleteItems" is granted.
   * @returns {boolean}
   */
  canDeleteOrgItems(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.deleteItems;
  }

  /**
   * Checks whether the privilege "portal:admin:reassignItems" is granted.
   * @returns {boolean}
   */
  canReassignOrgItems(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.reassignItems;
  }

  /**
   * Checks if isAdmin or has "portal:admin:viewUsers" and "portal:admin:viewItems"
   * and has at least one of these "portal:admin:updateItems", "portal:admin:deleteItems", or "portal:admin:reassignItems"
   * @returns {boolean}
   */
  canManageItems(): boolean {
    return (
      this.isAdmin() ||
      (this.canViewUsers() &&
        this.canViewOrgItems() &&
        (this.canUpdateOrgItems() ||
          this.canDeleteOrgItems() ||
          this.canReassignOrgItems()))
    );
  }

  /**
   * Checks whether the privilege "portal:admin:manageLicenses" is granted.
   * @returns {boolean}
   */
  canManageLicenses(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageLicenses;
  }

  /**
   * Checks whether the privilege "portal:admin:shareToGroup" is granted.
   * @returns {boolean}
   */
  canShareOthersItemsToGroup(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.shareToGroup;
  }

  /**
   * Checks whether the privilege "portal:admin:shareToOrg" is granted.
   * @returns {boolean}
   */
  canShareOthersItemsToOrg(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.shareToOrg;
  }

  /**
   * Checks whether the privilege "portal:admin:shareToPublic" is granted.
   * @returns {boolean}
   */
  canShareOthersItemsToPublic(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.shareToPublic;
  }

  /**
   * Checks whether the privilege "portal:admin:createUpdateCapableGroup" is granted.
   * @returns {boolean}
   */
  canCreateUpdateCapableGroup(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.admin.createUpdateCapableGroup
    );
  }

  /**
   * Checks whether the privilege "portal:admin:updateItemCategorySchema" is granted.
   * @returns {boolean}
   */
  canUpdateItemCategories(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.admin.updateItemCategorySchema
    );
  }

  /**
   * Checks whether the privilege "portal:admin:manageCollaborations" is granted.
   * @returns {boolean}
   */
  canManageCollaborations(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.admin.manageCollaborations
    );
  }

  /**
   * Checks whether the privilege "portal:admin:manageSecurity" is granted.
   * @returns {boolean}
   */
  canManageSecurity(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageSecurity;
  }

  /**
   * Checks whether the privilege "portal:admin:manageWebsite" is granted.
   * @returns {boolean}
   */
  canManageWebsite(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageWebsite;
  }

  /**
   * Checks whether the privilege "portal:admin:manageRoles" is granted.
   * @returns {boolean}
   */
  canManageRoles(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageRoles;
  }

  /**
   * Checks whether the privilege "portal:admin:manageServers" is granted.
   * @returns {boolean}
   */
  canManageServers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageServers;
  }

  /**
   * Checks whether the privilege "portal:admin:manageUtilityServices" is granted.
   * @returns {boolean}
   */
  canManageUtilityServices(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.admin.manageUtilityServices
    );
  }

  /**
   * Checks whether the privilege "portal:admin:manageCredits" is granted.
   * @returns {boolean}
   */
  canManageCredits(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.admin.manageCredits;
  }

  /**
   * Checks whether the privilege "portal:user:createGroup" is granted.
   * @returns {boolean}
   */
  canCreateGroup(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.createGroup;
  }

  /**
   * Checks whether the privilege "portal:user:joinGroup" is granted.
   * @returns {boolean}
   */
  canJoinOrgGroup(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.joinGroup;
  }

  /**
   * Checks whether the privilege "portal:user:joinNonOrgGroup" is granted.
   * @returns {boolean}
   */
  canJoinNonOrgGroup(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.joinNonOrgGroup;
  }

  /**
   * Checks whether the privilege "portal:publisher:publishFeatures" is granted.
   * @returns {boolean}
   */
  canPublishFeatures(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.publisher.publishFeatures
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:publishTiles" is granted.
   * @returns {boolean}
   */
  canPublishTiles(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.publisher.publishTiles;
  }

  /**
   * Checks whether the privilege "portal:publisher:publishScenes" is granted.
   * @returns {boolean}
   */
  canPublishScenes(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.publisher.publishScenes
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:publishServerServices" is granted.
   * @returns {boolean}
   */
  canPublishServerServices(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.publisher.publishServerServices
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:publishServerGPServices" is granted.
   * @returns {boolean}
   */
  canPublishServerGPServices(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.publisher.publishServerGPServices
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:publishVectorTiles" is granted.
   * Eventually we will have this privilege. For now, return true here.
   * @returns {boolean}
   *
   */
  canPublishVectorTiles(): boolean {
    return true;
  }

  /**
   * Checks whether the privilege "portal:publisher:publishImageCollection" is granted.
   * Eventually we will have this privilege. For now only check server publishing permission.
   * @returns {boolean}
   */
  canPublishImageCollection(): boolean {
    return this.canPublishServerServices();
  }

  /**
   * Checks whether the privilege "portal:user:createItem" is granted.
   * @returns {boolean}
   */
  canCreateItem(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.createItem;
  }

  /**
   * Checks whether the privilege "portal:user:shareToGroup" is granted.
   * @returns {boolean}
   */
  canShareItemToGroup(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.shareToGroup;
  }

  /**
   * Checks whether the privilege "portal:user:shareToOrg" is granted.
   * @returns {boolean}
   */
  canShareItemToOrg(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.shareToOrg;
  }

  /**
   * Checks whether the privilege "portal:user:shareToPublic" is granted.
   * @returns {boolean}
   */
  canShareItemToPublic(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.shareToPublic;
  }

  /**
   * Checks whether the privilege "portal:user:shareGroupToOrg" is granted.
   * @returns {boolean}
   */
  canShareGroupToOrg(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.shareGroupToOrg;
  }

  /**
   * Checks whether the privilege "portal:user:shareGroupToPublic" is granted.
   * @returns {boolean}
   */
  canShareGroupToPublic(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.user.shareGroupToPublic
    );
  }

  /**
   * Checks whether the privilege "portal:user:viewOrgUsers" is granted.
   * @returns {boolean}
   */
  canViewOrgUsers(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.viewOrgUsers;
  }

  /**
   * Checks whether the privilege "portal:user:viewOrgItems" is granted.
   * @returns {boolean}
   */
  canViewOrgUserContent(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.viewOrgItems;
  }

  /**
   * Checks whether the privilege "portal:user:viewOrgGroups" is granted.
   * @returns {boolean}
   */
  canViewOrgUserGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.viewOrgGroups;
  }

  /**
   * Checks whether the privilege "portal:user:viewTracks" is granted.
   */
  canViewTracks(): boolean {
    return this.privilegeObj && this.privilegeObj.portal.user.viewTracks;
  }

  /**
   * Checks whether the privilege "features:user:edit" is granted.
   * @returns {boolean}
   */
  canEditFeatures(): boolean {
    return this.privilegeObj && this.privilegeObj.features.user.edit;
  }

  /**
   * Checks whether the privilege "features:user:fullEdit" is granted.
   * @returns {boolean}
   */
  canEditFeaturesFullControl(): boolean {
    return this.privilegeObj && this.privilegeObj.features.user.fullEdit;
  }

  /**
   * Checks whether the privilege "opendata:user:openDataAdmin" is granted.
   * @returns {boolean}
   */
  canManageOpenData(): boolean {
    return this.privilegeObj && this.privilegeObj.opendata.user.openDataAdmin;
  }

  /**
   * Checks whether the privilege "opendata:user:designateGroup" is granted.
   * @returns {boolean}
   */
  canDesignateOpenDataGroups(): boolean {
    return this.privilegeObj && this.privilegeObj.opendata.user.designateGroup;
  }

  /**
   * Checks whether the privilege "premium:user:geocode" is granted.
   * @returns {boolean}
   */
  canUseGeocode(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.geocode;
  }

  /**
   * Checks whether the privilege "premium:user:networkanalysis" is granted.
   * @returns {boolean}
   */
  canUseNetworkAnalysis(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.networkanalysis;
  }

  /**
   * Checks whether the privilege "premium:user:spatialanalysis" is granted.
   * @returns {boolean}
   */
  canUseSpatialAnalysis(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.spatialanalysis;
  }

  /**
   * Checks whether the privilege "premium:user:geoenrichment" is granted.
   * @returns {boolean}
   */
  canUseGeoenrichment(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.geoenrichment;
  }

  /**
   * Checks whether the privilege "premium:user:demographics" is granted.
   * @returns {boolean}
   */
  canUseDemographics(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.demographics;
  }

  // Removed - https://devtopia.esri.com/WebGIS/arcgis-portal-app/issues/21382
  // /**
  //  * Checks whether the privilege "premium:user:elevation" is granted.
  //  * @returns {boolean}
  //  */
  // public canUseElevation(): boolean {
  //   return this.privilegeObj && this.privilegeObj.premium.user.elevation;
  // }

  /**
   * Checks whether the privilege "premium:user:featurereport" is granted.
   * @returns {boolean}
   */
  canUseFeatureReport(): boolean {
    return this.privilegeObj && this.privilegeObj.premium.user.featurereport;
  }

  /**
   * Checks whether the privilege "premium:publisher:geoanalytics" is granted.
   * @returns {boolean}
   */
  canUseGeoanalytics(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.premium.publisher.geoanalytics
    );
  }

  /**
   * Checks whether the privilege "premium:publisher:rasteranalysis" is granted.
   * @returns {boolean}
   */
  canUseRasterAnalysis(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.premium.publisher.rasteranalysis
    );
  }

  /**
   * Checks whether the privilege "marketplace:admin:purchase" is granted.
   * @returns {boolean}
   */
  canPurchaseMarketplace(): boolean {
    return this.privilegeObj && this.privilegeObj.marketplace.admin.purchase;
  }

  /**
   * Checks whether the privilege "marketplace:admin:manage" is granted.
   * @returns {boolean}
   */
  canManageMarketplace(): boolean {
    return this.privilegeObj && this.privilegeObj.marketplace.admin.manage;
  }

  /**
   * Checks whether the privilege "marketplace:admin:startTrial" is granted.
   * @returns {boolean}
   */
  canTrialMarketplace(): boolean {
    return this.privilegeObj && this.privilegeObj.marketplace.admin.startTrial;
  }

  /**
   * Checks whether the privilege "premium:publisher:createNotebooks" is granted.
   * @returns {boolean}
   */
  canCreateNotebooks(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.premium.publisher.createNotebooks
    );
  }

  /**
   * Checks whether the privilege "premium:publisher:scheduleNotebooks" is granted.
   * @returns {boolean}
   */
  canScheduleNotebooks(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.premium.publisher.scheduleNotebooks
    );
  }

  /**
   * Checks whether the privilege "premium:publisher:publishNotebooks" is granted.
   * @returns {boolean}
   */
  canPublishNotebooks(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.premium.publisher.publishNotebooks
    );
  }

  /**
   * Checks whether the privilege "premium:publisher:createAdvancedNotebooks" is granted.
   * @returns {boolean}
   */
  canCreateAdvancedNotebooks(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.premium.publisher.createAdvancedNotebooks
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:bulkPublishFromDataStores" is granted.
   * @returns {boolean}
   */
  canBulkPublishFromDataStores(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.publisher.bulkPublishFromDataStores
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:enumerateDataStores" is granted.
   * @returns {boolean}
   */
  canEnumerateDataStores(): boolean {
    return (
      this.privilegeObj &&
      this.privilegeObj.portal.publisher.enumerateDataStores
    );
  }

  /**
   * Checks whether the privilege "portal:publisher:registerDataStores" is granted.
   * @returns {boolean}
   */
  canRegisterDataStores(): boolean {
    return (
      this.privilegeObj && this.privilegeObj.portal.publisher.registerDataStores
    );
  }

  /**
   * Sets the roles privileges to those passed as input. Overwrites whatever privileges were defined for the
   * role beforehand. If passed null or undefined or an non-standard privileges object, it will reset the privileges
   * to default; all denied.
   * @param privileges
   */
  setPrivileges(privileges: string[]): void {
    this.privileges = privileges;
    this._initPrivilegesObject();
    if (privileges) {
      this._readPrivileges(privileges);
    }
  }

  // ┌─────────┐
  // │ Private │
  // └─────────┘

  /**
   * Initialize the internal privileges object.
   * @private
   */
  private _initPrivilegesObject(): void {
    this._applyToAll(false); // reset all to false
  }

  /**
   * Read the privileges.
   * @private
   */
  private _readPrivileges(privileges: string[]) {
    if (privileges && privileges instanceof Array) {
      privileges.forEach(privilege => {
        this._readPrivilege(privilege);
      });
    }
  }

  /**
   * Read the privilege string.
   * @param privilege
   * @private
   */
  private _readPrivilege(privilege: string): void {
    this._applyEffectToAction(true, privilege); // we're only given the allowed privileges
  }

  /**
   * Apply the grant effect to the appropriate privilege.
   * @param allow
   * @param action
   * @private
   */
  private _applyEffectToAction(allow: boolean, action: string): void {
    switch (action) {
      case "portal:admin:inviteUsers":
        this.privilegeObj.portal.admin.inviteUsers = allow;
        break;
      case "portal:admin:disableUsers":
        this.privilegeObj.portal.admin.disableUsers = allow;
        break;
      case "portal:admin:viewUsers":
        this.privilegeObj.portal.admin.viewUsers = allow;
        break;
      case "portal:admin:updateUsers":
        this.privilegeObj.portal.admin.updateUsers = allow;
        break;
      case "portal:admin:deleteUsers":
        this.privilegeObj.portal.admin.deleteUsers = allow;
        break;
      case "portal:admin:changeUserRoles":
        this.privilegeObj.portal.admin.changeUserRoles = allow;
        break;
      case "portal:admin:viewGroups":
        this.privilegeObj.portal.admin.viewGroups = allow;
        break;
      case "portal:admin:updateGroups":
        this.privilegeObj.portal.admin.updateGroups = allow;
        break;
      case "portal:admin:deleteGroups":
        this.privilegeObj.portal.admin.deleteGroups = allow;
        break;
      case "portal:admin:reassignGroups":
        this.privilegeObj.portal.admin.reassignGroups = allow;
        break;
      case "portal:admin:assignToGroups":
        this.privilegeObj.portal.admin.assignToGroups = allow;
        break;
      case "portal:admin:manageEnterpriseGroups":
        this.privilegeObj.portal.admin.manageEnterpriseGroups = allow;
        break;
      case "portal:admin:viewItems":
        this.privilegeObj.portal.admin.viewItems = allow;
        break;
      case "portal:admin:updateItems":
        this.privilegeObj.portal.admin.updateItems = allow;
        break;
      case "portal:admin:deleteItems":
        this.privilegeObj.portal.admin.deleteItems = allow;
        break;
      case "portal:admin:reassignItems":
        this.privilegeObj.portal.admin.reassignItems = allow;
        break;
      case "portal:admin:manageLicenses":
        this.privilegeObj.portal.admin.manageLicenses = allow;
        break;
      case "portal:admin:shareToGroup":
        this.privilegeObj.portal.admin.shareToGroup = allow;
        break;
      case "portal:admin:shareToOrg":
        this.privilegeObj.portal.admin.shareToOrg = allow;
        break;
      case "portal:admin:shareToPublic":
        this.privilegeObj.portal.admin.shareToPublic = allow;
        break;
      case "portal:admin:createUpdateCapableGroup":
        this.privilegeObj.portal.admin.createUpdateCapableGroup = allow;
        break;
      case "portal:admin:updateItemCategorySchema":
        this.privilegeObj.portal.admin.updateItemCategorySchema = allow;
        break;
      case "portal:admin:manageCollaborations":
        this.privilegeObj.portal.admin.manageCollaborations = allow;
        break;
      case "portal:admin:manageSecurity":
        this.privilegeObj.portal.admin.manageSecurity = allow;
        break;
      case "portal:admin:manageWebsite":
        this.privilegeObj.portal.admin.manageWebsite = allow;
        break;
      case "portal:admin:manageRoles":
        this.privilegeObj.portal.admin.manageRoles = allow;
        break;
      case "portal:admin:manageServers":
        this.privilegeObj.portal.admin.manageServers = allow;
        break;
      case "portal:admin:manageUtilityServices":
        this.privilegeObj.portal.admin.manageUtilityServices = allow;
        break;
      case "portal:admin:manageCredits":
        this.privilegeObj.portal.admin.manageCredits = allow;
        break;
      case "portal:publisher:publishFeatures":
        this.privilegeObj.portal.publisher.publishFeatures = allow;
        break;
      case "portal:publisher:publishTiles":
        this.privilegeObj.portal.publisher.publishTiles = allow;
        break;
      case "portal:publisher:publishScenes":
        this.privilegeObj.portal.publisher.publishScenes = allow;
        break;
      case "portal:publisher:publishServerServices":
        this.privilegeObj.portal.publisher.publishServerServices = allow;
        break;
      case "portal:publisher:publishServerGPServices":
        this.privilegeObj.portal.publisher.publishServerGPServices = allow;
        break;
      case "portal:publisher:bulkPublishFromDataStores":
        this.privilegeObj.portal.publisher.bulkPublishFromDataStores = allow;
        break;
      case "portal:publisher:enumerateDataStores":
        this.privilegeObj.portal.publisher.enumerateDataStores = allow;
        break;
      case "portal:publisher:registerDataStores":
        this.privilegeObj.portal.publisher.registerDataStores = allow;
        break;
      case "portal:user:createGroup":
        this.privilegeObj.portal.user.createGroup = allow;
        break;
      case "portal:user:joinGroup":
        this.privilegeObj.portal.user.joinGroup = allow;
        break;
      case "portal:user:joinNonOrgGroup":
        this.privilegeObj.portal.user.joinNonOrgGroup = allow;
        break;
      case "portal:user:createItem":
        this.privilegeObj.portal.user.createItem = allow;
        break;
      case "portal:user:shareToGroup":
        this.privilegeObj.portal.user.shareToGroup = allow;
        break;
      case "portal:user:shareToOrg":
        this.privilegeObj.portal.user.shareToOrg = allow;
        break;
      case "portal:user:shareToPublic":
        this.privilegeObj.portal.user.shareToPublic = allow;
        break;
      case "portal:user:shareGroupToOrg":
        this.privilegeObj.portal.user.shareGroupToOrg = allow;
        break;
      case "portal:user:shareGroupToPublic":
        this.privilegeObj.portal.user.shareGroupToPublic = allow;
        break;
      case "portal:user:viewOrgUsers":
        this.privilegeObj.portal.user.viewOrgUsers = allow;
        break;
      case "portal:user:viewOrgItems":
        this.privilegeObj.portal.user.viewOrgItems = allow;
        break;
      case "portal:user:viewOrgGroups":
        this.privilegeObj.portal.user.viewOrgGroups = allow;
        break;
      case "portal:user:viewTracks":
        this.privilegeObj.portal.user.viewTracks = allow;
        break;
      case "features:user:edit":
        this.privilegeObj.features.user.edit = allow;
        break;
      case "features:user:fullEdit":
        this.privilegeObj.features.user.fullEdit = allow;
        break;
      case "opendata:user:openDataAdmin":
        this.privilegeObj.opendata.user.openDataAdmin = allow;
        break;
      case "opendata:user:designateGroup":
        this.privilegeObj.opendata.user.designateGroup = allow;
        break;
      case "premium:user:geocode":
        this.privilegeObj.premium.user.geocode = allow;
        break;
      case "premium:user:networkanalysis":
        this.privilegeObj.premium.user.networkanalysis = allow;
        break;
      case "premium:user:spatialanalysis":
        this.privilegeObj.premium.user.spatialanalysis = allow;
        break;
      case "premium:user:geoenrichment":
        this.privilegeObj.premium.user.geoenrichment = allow;
        break;
      case "premium:user:demographics":
        this.privilegeObj.premium.user.demographics = allow;
        break;
      // Removed - https://devtopia.esri.com/WebGIS/arcgis-portal-app/issues/21382
      // case "premium:user:elevation":
      //   this.privilegeObj.premium.user.elevation = allow;
      //   break;
      case "premium:user:featurereport":
        this.privilegeObj.premium.user.featurereport = allow;
        break;
      case "premium:publisher:geoanalytics":
        this.privilegeObj.premium.publisher.geoanalytics = allow;
        break;
      case "premium:publisher:rasteranalysis":
        this.privilegeObj.premium.publisher.rasteranalysis = allow;
        break;
      case "marketplace:admin:purchase":
        this.privilegeObj.marketplace.admin.purchase = allow;
        break;
      case "marketplace:admin:manage":
        this.privilegeObj.marketplace.admin.manage = allow;
        break;
      case "marketplace:admin:startTrial":
        this.privilegeObj.marketplace.admin.startTrial = allow;
        break;
      case "premium:publisher:createNotebooks":
        this.privilegeObj.premium.publisher.createNotebooks = allow;
        break;
      case "premium:publisher:scheduleNotebooks":
        this.privilegeObj.premium.publisher.scheduleNotebooks = allow;
        break;
      case "premium:publisher:publishNotebooks":
        this.privilegeObj.premium.publisher.publishNotebooks = allow;
        break;
      case "premium:publisher:createAdvancedNotebooks":
        this.privilegeObj.premium.publisher.createAdvancedNotebooks = allow;
        break;
      default:
        break;
    }
  }

  /**
   * Apply the grant effect to all privileges.
   * @param allow
   * @private
   */
  private _applyToAll(allow: boolean): void {
    this.privilegeObj.portal.admin.inviteUsers = allow;
    this.privilegeObj.portal.admin.disableUsers = allow;
    this.privilegeObj.portal.admin.viewUsers = allow;
    this.privilegeObj.portal.admin.updateUsers = allow;
    this.privilegeObj.portal.admin.deleteUsers = allow;
    this.privilegeObj.portal.admin.changeUserRoles = allow;
    this.privilegeObj.portal.admin.viewGroups = allow;
    this.privilegeObj.portal.admin.updateGroups = allow;
    this.privilegeObj.portal.admin.deleteGroups = allow;
    this.privilegeObj.portal.admin.reassignGroups = allow;
    this.privilegeObj.portal.admin.assignToGroups = allow;
    this.privilegeObj.portal.admin.manageEnterpriseGroups = allow;
    this.privilegeObj.portal.admin.viewItems = allow;
    this.privilegeObj.portal.admin.updateItems = allow;
    this.privilegeObj.portal.admin.deleteItems = allow;
    this.privilegeObj.portal.admin.reassignItems = allow;
    this.privilegeObj.portal.admin.manageLicenses = allow;
    this.privilegeObj.portal.admin.shareToGroup = allow;
    this.privilegeObj.portal.admin.shareToOrg = allow;
    this.privilegeObj.portal.admin.shareToPublic = allow;
    this.privilegeObj.portal.admin.createUpdateCapableGroup = allow;
    this.privilegeObj.portal.admin.updateItemCategorySchema = allow;
    this.privilegeObj.portal.admin.manageCollaborations = allow;
    this.privilegeObj.portal.admin.manageSecurity = allow;
    this.privilegeObj.portal.admin.manageWebsite = allow;
    this.privilegeObj.portal.admin.manageRoles = allow;
    this.privilegeObj.portal.admin.manageServers = allow;
    this.privilegeObj.portal.admin.manageUtilityServices = allow;
    this.privilegeObj.portal.admin.manageCredits = allow;
    this.privilegeObj.portal.publisher.publishFeatures = allow;
    this.privilegeObj.portal.publisher.publishTiles = allow;
    this.privilegeObj.portal.publisher.publishScenes = allow;
    this.privilegeObj.portal.publisher.publishServerServices = allow;
    this.privilegeObj.portal.publisher.publishServerGPServices = allow;
    this.privilegeObj.portal.user.createGroup = allow;
    this.privilegeObj.portal.user.joinGroup = allow;
    this.privilegeObj.portal.user.joinNonOrgGroup = allow;
    this.privilegeObj.portal.user.createItem = allow;
    this.privilegeObj.portal.user.shareToGroup = allow;
    this.privilegeObj.portal.user.shareToOrg = allow;
    this.privilegeObj.portal.user.shareToPublic = allow;
    this.privilegeObj.portal.user.shareGroupToOrg = allow;
    this.privilegeObj.portal.user.shareGroupToPublic = allow;
    this.privilegeObj.portal.user.viewOrgUsers = allow;
    this.privilegeObj.portal.user.viewOrgItems = allow;
    this.privilegeObj.portal.user.viewOrgGroups = allow;
    this.privilegeObj.portal.user.viewTracks = allow;
    this.privilegeObj.features.user.edit = allow;
    this.privilegeObj.opendata.user.openDataAdmin = allow;
    this.privilegeObj.opendata.user.designateGroup = allow;
    this.privilegeObj.premium.user.geocode = allow;
    this.privilegeObj.premium.user.networkanalysis = allow;
    this.privilegeObj.premium.user.spatialanalysis = allow;
    this.privilegeObj.premium.user.geoenrichment = allow;
    this.privilegeObj.premium.user.demographics = allow;
    // Removed - https://devtopia.esri.com/WebGIS/arcgis-portal-app/issues/21382
    // this.privilegeObj.premium.user.elevation = allow;
    this.privilegeObj.premium.user.featurereport = allow;
    this.privilegeObj.premium.publisher.geoanalytics = allow;
    this.privilegeObj.premium.publisher.rasteranalysis = allow;
    this.privilegeObj.marketplace.admin.purchase = allow;
    this.privilegeObj.marketplace.admin.manage = allow;
    this.privilegeObj.marketplace.admin.startTrial = allow;
    this.privilegeObj.premium.publisher.createNotebooks = allow;
    this.privilegeObj.premium.publisher.scheduleNotebooks = allow;
    this.privilegeObj.premium.publisher.publishNotebooks = allow;
    this.privilegeObj.premium.publisher.createAdvancedNotebooks = allow;
  }
}

export default Role;
